/**
 * @file mixin to help with file upload flow
 */
import { trackEvent } from 'bits/analytics'
import { __ } from 'bits/intl'
import { FILE_TOO_LARGE_ERROR, UploadJob } from 'bits/uploader'
import Vue from 'vue'
import { Component, Prop } from 'vue-property-decorator'

@Component({})
export default class UploadFileMixin extends Vue {
  @Prop({ default: null }) file!: File | null
  @Prop() name!: string
  @Prop({ default: 31457280 }) maxFileSize!: number // default is 30MB

  error = ''
  canRetryUpload = true
  progress = 0

  _uploadJob?: UploadJob

  get filename(): string {
    return this.name || this.file?.name || __('Untitled')
  }

  get fileUrl(): string {
    return this.file ? URL.createObjectURL(this.file) : ''
  }

  cancelUpload(): void {
    if (this._uploadJob) {
      this._uploadJob.cancel()
    }
    this.$emit('cancel', this.file)
  }

  onDone(): void {
    // implement your own
  }

  _onDone(response): void {
    trackEvent('Uploader', 'Done')
    this.onDone()
    this.$emit('done', this.file, response.url)
  }

  _onError(response: Record<string, unknown> = {}): void {
    if (response.error == FILE_TOO_LARGE_ERROR) {
      this.$emit('reject', this.file, { size: true })
      trackEvent('Uploader', 'Reject')
    } else {
      const errorText = __('Sorry, there was an error uploading your file.')
      this.error = errorText
      this.canRetryUpload = true
      trackEvent('Uploader', 'Error')
      this.$emit('error', this.file, errorText)
    }
  }

  onProgress(): void {
    // implement your own
  }
  _onProgress(progress): void {
    this.progress = progress
    this.onProgress()
  }

  startUpload(): void {
    this.error = ''
    const onDone = this._onDone.bind(this)
    const onError = this._onError.bind(this)
    const onProgress = this._onProgress.bind(this)
    this._uploadJob = new UploadJob(this.file, { maxFileSize: this.maxFileSize })
    this._uploadJob.on('done', onDone)
    this._uploadJob.on('error', onError)
    this._uploadJob.on('progress', onProgress)
    this._uploadJob.perform()
  }

  mounted(): void {
    if (this.file) {
      this.startUpload()
    }
  }

  beforeDestroy(): void {
    if (this._uploadJob) {
      this._uploadJob.cancel()
    }
  }
}
